<script setup lang="ts">
import type { IDepositCard, IDepositIncome } from '~/components/entity/deposit/item/types'
import type { IEntityPosition } from '~/components/entity/base-entity'
import { depositForWhomKeys } from '~/components/entity/deposit'
import { Badge } from '~/components/ui/badge'
import { UiCardTop } from '~/components/ui/card'
import { LucideScale, LucideX } from 'lucide-vue-next'
import { Rating } from '~/components/ui/rating'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'

const props = withDefaults(
  defineProps<{
    item: IDepositCard
    hideOrganizationDetails?: boolean
    hideCompare?: boolean
    showTypes?: boolean
    type?: 'bank' | 'microcredit'
    position?: IEntityPosition
    selectedCurrency?: string
    /** When set, the card shows the income on the user's amount instead of the deposit's headline rate and term. */
    income?: IDepositIncome
  }>(),
  {
    hideOrganizationDetails: false,
    hideCompare: false,
    showTypes: false,
    type: 'bank',
    position: undefined,
    selectedCurrency: undefined,
    income: undefined
  }
)

const { t } = useI18n()

const normalizedSelectedCurrency = computed(() => props.selectedCurrency?.toLowerCase())

const isHighlighted = useEntityHighlight(props.item, props.position)

const depositTypes = computed(() => {
  return props.item.types?.map((type) => type.title) || []
})

const depositForWhom = computed(() => {
  const result: string[] = []
  const forWhom = props.item.for_whom
  if (forWhom) {
    for (const key in forWhom) {
      if (forWhom[key] && depositForWhomKeys[key]) {
        result.push(t(depositForWhomKeys[key]))
      }
    }
  }
  return result
})

// The effective rate is worth a separate line only when it differs from the nominal one.
const hasDistinctEffectiveRate = computed(
  () => !!props.income?.effectiveRate && props.income.effectiveRate !== props.income.rate
)

const incomeTermLabel = computed(() =>
  props.income?.isOptimalTerm
    ? t('entity.deposit.item.optimal_term')
    : t('entity.deposit.item.deposit_term')
)

const compareStore = useCompareStore()
const isInCompare = computed(() => compareStore.has('deposits', props.item.id))
const atLimit = computed(() => compareStore.countByType.deposits >= 10)
const compareLabel = computed(() => (isInCompare.value ? t('compare.remove') : t('compare.add')))

const toggleCompare = () => {
  if (isInCompare.value) {
    compareStore.remove('deposits', props.item.id)
  } else {
    compareStore.add('deposits', props.item.id)
  }
}

// The title may carry the search highlight (<em>); attributes need plain text
const plainTitle = computed(() => useStripTags(props.item.title))
</script>

<template>
  <article :class="[isHighlighted ? 'CardPremium group' : 'Card']">
    <UiCardTop v-if="isHighlighted" />
    <div class="EntityCardLayout">
      <div class="flex gap-4">
        <EntityOrganizationLogo
          v-if="!props.hideOrganizationDetails"
          :src="props.item.organization.logo"
          :alt="props.item.organization.title"
          :to="props.item.organization.url"
          :aria-label="
            $t('entity.deposit.item.more_about_org', { org: props.item.organization.title })
          "
          :highlighted="isHighlighted"
        />
        <div class="flex flex-col items-start">
          <NuxtLinkLocale
            :to="props.item.url"
            :aria-label="
              $t('entity.deposit.item.more_about_deposit_from', {
                title: plainTitle,
                org: props.item.organization.title
              })
            "
            class="inline-block hover:underline"
          >
            <Heading tag="h3" class="hit-title h6"><span v-hydrated-html="props.item.title" /></Heading>
          </NuxtLinkLocale>
          <NuxtLinkLocale
            v-if="!props.hideOrganizationDetails"
            :to="props.item.organization.url"
            :aria-label="
              $t('entity.deposit.item.more_about_org', { org: props.item.organization.title })
            "
            class="CardOrgLink"
          >
            {{ props.item.organization.title }}
          </NuxtLinkLocale>
          <div v-if="props.item.ratings" class="flex items-center">
            <Rating :value="Number(props.item.ratings)" size="xs" />
            <div class="ml-2 text-sm text-gray-900">{{ props.item.ratings }}</div>
          </div>
        </div>
      </div>
      <div v-if="props.income" class="grid grid-cols-2 gap-y-2 sm:grid-cols-4">
        <div>
          <div class="SecondaryTextHeadings">{{ $t('common.annual_rate') }}</div>
          <div class="font-semibold" data-testid="deposit-income-rate">
            {{ useRateFormat(props.income.rate) }}%
          </div>
          <div v-if="hasDistinctEffectiveRate" class="text-xs text-gray-500">
            {{
              $t('entity.deposit.item.effective_rate', {
                rate: useRateFormat(props.income.effectiveRate)
              })
            }}
          </div>
        </div>
        <div>
          <div class="SecondaryTextHeadings">{{ incomeTermLabel }}</div>
          <div class="font-semibold" data-testid="deposit-income-term">
            {{ $t('entity.deposit.item.months', { n: props.income.months }) }}
          </div>
        </div>
        <div>
          <div class="SecondaryTextHeadings">{{ $t('entity.deposit.item.income') }}</div>
          <div class="font-semibold text-green-600" data-testid="deposit-income-amount">
            +{{ useCurrencyFormat(props.income.income, props.income.currencyId) }}
          </div>
        </div>
        <div>
          <div class="SecondaryTextHeadings">{{ $t('entity.deposit.item.total') }}</div>
          <div class="font-semibold">
            {{ useCurrencyFormat(props.income.total, props.income.currencyId) }}
          </div>
        </div>
      </div>
      <div v-else class="grid grid-cols-2 gap-y-2 sm:grid-cols-3">
        <div class="sm:col-span-1">
          <div class="SecondaryTextHeadings">{{ $t('common.annual_rate') }}</div>
          <div class="font-semibold">{{ props.item.best_rate }}</div>
        </div>
        <div v-if="props.item.max_period" class="sm:col-span-1">
          <div class="SecondaryTextHeadings">{{ $t('entity.deposit.item.deposit_term') }}</div>
          <div class="font-semibold">{{ props.item.max_period }}</div>
        </div>
        <div
          v-if="props.item.currencies && props.item.currencies.length"
          class="col-span-2 sm:col-span-1"
        >
          <div class="SecondaryTextHeadings">{{ $t('common.currency') }}</div>
          <div class="font-semibold">
            <template v-for="(currency, index) in props.item.currencies" :key="currency.code">
              <span
                :class="{
                  'text-blue-600':
                    normalizedSelectedCurrency &&
                    currency.code.toLowerCase() === normalizedSelectedCurrency
                }"
                >{{ currency.code }}</span
              ><span v-if="index < props.item.currencies.length - 1">, </span>
            </template>
          </div>
        </div>
      </div>
    </div>
    <footer
      :class="[
        { 'gap-2': (depositTypes.length || depositForWhom.length) && isHighlighted },
        'mt-4 flex flex-wrap items-start'
      ]"
    >
      <div class="flex flex-1 flex-wrap gap-2">
        <template
          v-if="
            (depositTypes.length || depositForWhom.length) && (isHighlighted || props.showTypes)
          "
        >
          <Badge
            v-for="forWhom in depositForWhom"
            :key="forWhom"
            variant="gray"
            class="text-xs"
            rounded="full"
          >
            {{ forWhom }}
          </Badge>
          <Badge
            v-for="depositType in depositTypes"
            :key="depositType"
            variant="gray"
            class="text-xs"
            rounded="full"
          >
            {{ depositType }}
          </Badge>
        </template>
      </div>
      <div v-if="!hideCompare" class="flex w-full justify-end sm:-mt-1 sm:w-auto">
        <Button
          variant="ghost"
          size="sm"
          :disabled="!isInCompare && atLimit"
          class="CompareButton"
          @click="toggleCompare"
        >
          <LucideX v-show="isInCompare" class="size-3.5" />
          <LucideScale v-show="!isInCompare" class="size-3.5" />
          {{ compareLabel }}
        </Button>
      </div>
    </footer>
  </article>
</template>
